import { useCallback, useEffect, useRef, useState } from 'react';
import Dialogo from '@/components/Dialogo';
import { conectarCanal, mensagemDoErro, statusCanal } from '@/services/canaisService';
import { papelDoNumeroPorId, statusConexaoPorId } from '@/lib/constants';
import { formatarTelefone } from '@/lib/telefone';
import { tratarErro } from '@/lib/erros';

/**
 * Conexão de um canal por QR Code.
 *
 * Dois relógios correm juntos aqui, e é o que dá a complexidade desta tela:
 *
 * - **status a cada 3s** — o WhatsApp não avisa ninguém quando o pareamento
 *   termina; a única forma de saber é perguntar à Evolution. É o que faz a tela
 *   virar "Conectado" sozinha, sem o gestor precisar clicar em nada.
 * - **QR novo a cada 30s** — o QR do WhatsApp expira em segundos. Sem a
 *   renovação, quem demora a achar "Aparelhos conectados" no celular aponta a
 *   câmera para um código morto e conclui que a plataforma está quebrada.
 *
 * Os dois são limpos no fechamento e ao conectar. Timer que sobrevive ao modal
 * seguiria chamando a Evolution para sempre, de graça e sem ninguém olhando.
 */

const INTERVALO_STATUS_MS = 3_000;
const INTERVALO_QR_MS = 30_000;

export default function DialogoQrCanal({ aberto, aoFechar, clinicaId, canal }) {
  const [qr, setQr] = useState(null);
  const [erro, setErro] = useState(null);
  const [gerando, setGerando] = useState(false);
  const [estado, setEstado] = useState('desconectado');
  const [conectado, setConectado] = useState(false);
  const [confirmado, setConfirmado] = useState(false);

  // `vivo` evita setState depois do fechamento: as chamadas são assíncronas e
  // podem voltar com o componente já desmontado. Mesmo padrão do MidiaMensagem.
  const vivo = useRef(false);
  const timers = useRef({ status: null, qr: null });

  const numeroId = canal?.id ?? null;
  const papel = papelDoNumeroPorId(canal?.papel);

  /**
   * Reconectar um canal que está no ar derruba o número até alguém escanear de
   * novo — e um desses números é o WhatsApp vivo da recepção. Por isso o QR não
   * é gerado de saída: o gestor confirma primeiro. A confirmação mora aqui, e
   * não num `confirm()` antes de abrir, para que a decisão apareça junto da
   * consequência.
   */
  const jaConectado = canal?.evolution?.statusConexao === 'conectado';
  // `!conectado` é essencial, não defensivo: assim que o pareamento termina, o
  // servidor grava statusConexao='conectado' e o listener da lista traz esse
  // valor para cá — sem esta condição, a tela de sucesso seria substituída pelo
  // aviso de reconexão no instante em que o canal conecta.
  const precisaConfirmar = jaConectado && !confirmado && !conectado;

  const pararTimers = useCallback(() => {
    if (timers.current.status) clearInterval(timers.current.status);
    if (timers.current.qr) clearInterval(timers.current.qr);
    timers.current = { status: null, qr: null };
  }, []);

  const gerarQr = useCallback(async () => {
    if (!clinicaId || !numeroId) return;
    setGerando(true);
    setErro(null);
    try {
      const { data } = await conectarCanal({ clinicaId, numeroId });
      if (!vivo.current) return;
      setQr(data?.base64 ?? null);
      setEstado('aguardando_qr');
    } catch (e) {
      if (!vivo.current) return;
      console.error('[OdontoSDR] Falha ao gerar o QR:', e);
      setErro(mensagemDoErro(e, 'Não foi possível gerar o QR Code.'));
      setQr(null);
      // Os relógios seguem correndo de propósito. Uma VPS que piscou volta
      // sozinha na renovação seguinte, sem o gestor fazer nada; e parar aqui
      // deixaria o botão "Tentar de novo" gerando QR sem ninguém conferindo o
      // status — a tela nunca perceberia a conexão. A mensagem não pisca porque
      // só esta função escreve em `erro`; o polling de status falha calado.
    } finally {
      if (vivo.current) setGerando(false);
    }
  }, [clinicaId, numeroId]);

  const conferirStatus = useCallback(async () => {
    if (!clinicaId || !numeroId) return;
    try {
      const { data } = await statusCanal({ clinicaId, numeroId });
      if (!vivo.current) return;
      setEstado(data?.statusConexao ?? 'desconectado');
      if (data?.conectado) {
        setConectado(true);
        // Chegou ao fim: nada mais a perguntar, nada mais a renovar.
        pararTimers();
      }
    } catch (e) {
      /**
       * ⚠⚠ Falha de polling NÃO vira mensagem na tela — e isto é **decisão declarada**, não
       * esquecimento: o gestor está olhando para o QR, e um erro piscando **a cada 3 s** só
       * assustaria. A próxima volta provavelmente funciona.
       *
       * ★★ WT lote 1b: o que mudou é que ela passa a **REGISTRAR**. ⚠ O pedido do lote
       * mandava avisar na tela ("Não foi possível atualizar o QR"); ★ respeitei a decisão
       * de quem escreveu isto — a tela já mostra *"QR indisponível"* quando o QR de fato
       * falha, e é esse o caminho que o gestor precisa ver. **Um aviso a cada 3 s é o
       * oposto do objetivo deste lote.**
       */
      tratarErro('o status do canal', null, 'callable')(e);
    }
  }, [clinicaId, numeroId, pararTimers]);

  useEffect(() => {
    if (!aberto || !numeroId || precisaConfirmar) return undefined;

    vivo.current = true;
    setQr(null);
    setErro(null);
    setConectado(false);
    setEstado(canal?.evolution?.statusConexao ?? 'desconectado');

    gerarQr();
    timers.current.status = setInterval(conferirStatus, INTERVALO_STATUS_MS);
    timers.current.qr = setInterval(gerarQr, INTERVALO_QR_MS);

    return () => {
      vivo.current = false;
      pararTimers();
    };
    // `canal.evolution.statusConexao` de propósito fora das dependências: ele
    // muda a cada polling (o servidor grava 'aguardando_qr'), e reiniciaria o
    // efeito — jogando fora o QR na tela a cada 3 segundos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, numeroId, precisaConfirmar, gerarQr, conferirStatus, pararTimers]);

  function fechar() {
    pararTimers();
    vivo.current = false;
    setQr(null);
    setErro(null);
    setConectado(false);
    setConfirmado(false);
    aoFechar();
  }

  const status = statusConexaoPorId(estado);
  const titulo = jaConectado ? 'Reconectar canal' : 'Conectar canal';

  return (
    <Dialogo titulo={titulo} aberto={aberto} aoFechar={fechar} largura={440}>
      <div className="qr-canal">
        <header className="qr-canal-alvo">
          <strong>{papel.label}</strong>
          <span>{canal?.telefone ? formatarTelefone(canal.telefone) : '—'}</span>
        </header>

        {precisaConfirmar ? (
          <div className="qr-canal-confirma">
            <p className="qr-canal-aviso">
              Isso gera um novo QR e o número fica <strong>desconectado</strong> até você escanear
              de novo. Continuar?
            </p>
            <footer className="dialogo-acoes">
              <button type="button" className="btn-secundario" onClick={fechar}>
                Cancelar
              </button>
              <button type="button" className="btn-primario" onClick={() => setConfirmado(true)}>
                Continuar
              </button>
            </footer>
          </div>
        ) : conectado ? (
          <div className="qr-canal-sucesso">
            <p className="qr-canal-ok">Conectado ✓</p>
            <p className="qr-canal-instrucao">
              O canal já está trafegando. As mensagens deste número passam a aparecer na Central de
              Conversas.
            </p>
            <footer className="dialogo-acoes">
              <button type="button" className="btn-primario" onClick={fechar}>
                Concluir
              </button>
            </footer>
          </div>
        ) : (
          <>
            <div className="qr-canal-quadro">
              {qr ? (
                <img src={qr} alt="QR Code para conectar o WhatsApp" className="qr-canal-imagem" />
              ) : (
                <div className="qr-canal-vazio">
                  {erro ? 'QR indisponível' : 'Gerando o QR Code…'}
                </div>
              )}
            </div>

            {!erro && (
              <p className="qr-canal-instrucao">
                No WhatsApp desse número: <strong>Aparelhos conectados</strong> →{' '}
                <strong>Conectar aparelho</strong> → aponte para o QR.
              </p>
            )}

            {erro ? (
              <p className="erro">{erro}</p>
            ) : (
              <p className="qr-canal-estado">
                <span className="pilula pequena" style={{ background: status.cor }}>
                  {status.label}
                </span>
                <span className="qr-canal-nota">
                  O QR se renova sozinho a cada 30 segundos. A tela avisa quando conectar.
                </span>
              </p>
            )}

            <footer className="dialogo-acoes">
              <button type="button" className="btn-secundario" onClick={fechar}>
                Fechar
              </button>
              <button
                type="button"
                className="btn-primario"
                onClick={gerarQr}
                disabled={gerando}
              >
                {gerando ? 'Gerando…' : erro ? 'Tentar de novo' : 'Gerar outro QR'}
              </button>
            </footer>
          </>
        )}
      </div>
    </Dialogo>
  );
}
